import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, MessageSquare, Circle, Check, BarChart3 } from 'lucide-react';
import { useSmartAppBanner } from '../hooks/useSmartAppBanner';

// Different circle groups that will rotate through
const CIRCLE_GROUPS = [
  [
    'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop&crop=faces',
  ],
  [
    'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1552374196-c4e7ffc6e126?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=600&h=600&fit=crop&crop=faces',
  ],
  [
    'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1560087637-bf797bc7f3f3?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1557862921-37829c790f19?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1545167622-3a6ac756afa4?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1531427186611-ecfd6d936c79?w=600&h=600&fit=crop&crop=faces',
  ],
  [
    'https://images.unsplash.com/photo-1499996860823-5f82763f2b8d?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1496345875659-11f7dd282d1d?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1513956589380-bad6acb9b9d4?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=600&h=600&fit=crop&crop=faces',
    'https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?w=600&h=600&fit=crop&crop=faces',
  ],
];

// More attractive, diverse photos
const TOP_CIRCLE_PHOTOS = CIRCLE_GROUPS[0];

const BOTTOM_CIRCLE_PHOTOS = [
  'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=600&h=600&fit=crop&crop=faces',
  'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=600&h=600&fit=crop&crop=faces',
  'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=600&h=600&fit=crop&crop=faces',
  'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=600&h=600&fit=crop&crop=faces',
  'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=600&h=600&fit=crop&crop=faces',
  'https://images.unsplash.com/photo-1502823403499-6ccfcf4fb453?w=600&h=600&fit=crop&crop=faces',
];

export default function LandingPage() {
  useSmartAppBanner();

  return (
    <div className="min-h-screen bg-[#fafafa] text-gray-900 overflow-x-hidden">
      {/* Global Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;900&display=swap');
        * {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        .lowercase {
          text-transform: lowercase;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        /* Circle swipe away animation - smooth pop in, long hold, swipe out */
        @keyframes swipe-away {
          0% {
            opacity: 0;
            transform: scale(0.4) translateY(-70px);
          }
          10% {
            opacity: 1;
            transform: scale(1.08) translateY(0);
          }
          15% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
          85% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
          100% {
            opacity: 0;
            transform: scale(0.85) translateY(-180px);
          }
        }
        .circle-swipe-away {
          animation: swipe-away 3.5s cubic-bezier(0.3, 1.4, 0.5, 1) forwards;
          will-change: transform, opacity;
          backface-visibility: hidden;
          transform: translateZ(0);
          perspective: 1000px;
        }
        .circle-swipe-away img {
          opacity: 0;
          animation: fade-in 0.3s ease-out 0.15s forwards;
        }
        @keyframes fade-in {
          0% { opacity: 0; transform: scale(0.9); }
          100% { opacity: 1; transform: scale(1); }
        }
        .float {
          animation: float 4s ease-in-out infinite;
        }
        .pulse-glow {
          animation: pulse-glow 4s ease-in-out infinite;
        }
        .safe-padding {
          padding-left: max(1rem, env(safe-area-inset-left));
          padding-right: max(1rem, env(safe-area-inset-right));
        }
      `}</style>

      {/* Soft gradient orbs for visual interest */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10">
        <div className="absolute -top-32 -left-32 md:top-10 md:left-10 w-72 md:w-[500px] h-72 md:h-[500px] bg-gradient-to-br from-[#cef7fc] to-[#cef7fc]/50 rounded-full blur-[80px] md:blur-[100px] opacity-50 pulse-glow" />
        <div className="absolute bottom-10 -right-32 md:bottom-20 md:right-10 w-80 md:w-[600px] h-80 md:h-[600px] bg-gradient-to-br from-[#cef7fc]/60 to-[#cef7fc]/30 rounded-full blur-[80px] md:blur-[120px] opacity-40" />
      </div>

      {/* Navigation Bar */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 safe-padding">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo */}
            <div className="flex items-center gap-2 sm:gap-3">
              <div style={{ filter: 'drop-shadow(1px 1px 0px rgba(0, 0, 0, 0.9))' }}>
                <CirclesIcon size={28} color="#cef7fc" isActive={true} />
              </div>
              <span className="text-xl sm:text-2xl font-black lowercase text-neutral-900">circles</span>
            </div>
            
            {/* Get the App Button */}
            <a
              href="https://apps.apple.com/us/app/circles-meet-groups-together/id6757444421"
              className="bg-[#cef7fc] hover:bg-[#b8f0f7] text-neutral-900 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full font-semibold text-sm sm:text-base transition-all active:scale-[0.98] shadow-sm hover:shadow-md"
            >
              Get the app
            </a>
          </div>
        </div>
      </nav>

      {/* Hero - Mobile-first with better spacing */}
      <section className="pt-4 sm:pt-12 md:pt-16 pb-8 sm:pb-12 md:pb-16 px-4 sm:px-6 safe-padding">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-6 lg:gap-16 items-center">
            {/* Text Content - Centered on mobile */}
            <div className="space-y-4 sm:space-y-6 lg:pr-4 text-center lg:text-left order-2 lg:order-1">
              
              {/* Main Hero Message */}
              <div className="space-y-3 sm:space-y-5">
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black lowercase tracking-tight leading-[1.1] text-neutral-900">
                  meet new groups<br />
                  <span 
                    className="text-[#cef7fc]"
                    style={{ textShadow: '-1px -1px 0 #171717, 4px 4px 0 #171717' }}
                  >with your friends</span>
                </h1>
                <p className="text-base sm:text-lg md:text-xl text-neutral-500 leading-relaxed max-w-md mx-auto lg:mx-0">
                  vote together. if enough say yes, a group chat opens. send polls, make plans.
                </p>
              </div>
              
              {/* Download Buttons - App Store Style */}
              <div id="download" className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 pt-1 sm:pt-4">
                <a
                  href="https://apps.apple.com/us/app/circles-meet-groups-together/id6757444421"
                  className="w-full sm:w-auto bg-neutral-900 hover:bg-black text-white px-5 py-2.5 rounded-xl font-medium transition-all flex items-center justify-center gap-3 active:scale-[0.98] ring-2 ring-[#cef7fc]/40 hover:ring-[#cef7fc]/60"
                  style={{ boxShadow: '0 4px 0 rgba(206, 247, 252, 0.9)' }}
                >
                  <svg className="h-8 w-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div className="flex flex-col items-start">
                    <span className="text-[10px] leading-none opacity-80">Download on the</span>
                    <span className="text-lg leading-tight font-semibold -mt-0.5">App Store</span>
                  </div>
                </a>
                <div
                  className="w-full sm:w-auto bg-neutral-900/60 text-white/70 px-5 py-2.5 rounded-xl font-medium flex items-center justify-center gap-3 cursor-default"
                >
                  <svg className="h-7 w-7 opacity-70" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <div className="flex flex-col items-start">
                    <span className="text-[10px] leading-none opacity-80">COMING SOON TO</span>
                    <span className="text-lg leading-tight font-semibold -mt-0.5">Google Play</span>
                  </div>
                </div>
              </div>
            </div>

            {/* App Visual - Shows first on mobile */}
            <div className="flex justify-center lg:justify-end order-1 lg:order-2 w-full">
              <HeroPhoneVisual />
            </div>
          </div>
        </div>
      </section>

      {/* Group Chat Feature - commented out for now
      <section className="py-8 sm:py-12 md:py-16 px-4 sm:px-6 safe-padding border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 sm:gap-8 lg:gap-16 items-center">
            <div className="space-y-1.5 sm:space-y-3 lg:space-y-4 lg:pr-4 text-center lg:text-left">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-black lowercase leading-none text-neutral-900">group chat</h2>
              <p className="text-sm sm:text-base lg:text-lg text-neutral-500 leading-relaxed max-w-sm mx-auto lg:mx-0">
                matched circles get a group chat with built-in polls to plan hangouts together.
              </p>
            </div>
            <div className="flex justify-center lg:justify-end w-full">
              <div className="relative w-full max-w-xs sm:max-w-sm">
                <div className="bg-[#0b0b0b] rounded-2xl border border-[#1a1a1a] p-3 sm:p-4 shadow-xl">
                  <ChatVisual />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      */}

      {/* Available Cities */}
      <section className="py-8 sm:py-12 md:py-16 px-4 sm:px-6 safe-padding border-t border-gray-200">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#cef7fc]/20 text-neutral-700 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-5">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            rolling out city by city
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black lowercase text-neutral-900 mb-3 sm:mb-4">now live in utah</h2>
          <p className="text-sm sm:text-base text-neutral-500 mb-6 sm:mb-8 max-w-md mx-auto">
            we're live across utah — more states coming soon.
          </p>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-2.5">
            {[
              'Salt Lake City',
              'Provo',
              'Orem',
              'Ogden',
              'St. George',
              'Lehi',
              'Logan',
              'Layton',
              'Sandy',
              'Park City',
            ].map((city) => (
              <span
                key={city}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-neutral-900 text-white rounded-full text-xs sm:text-sm font-medium"
              >
                {city}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how" className="py-8 sm:py-12 md:py-16 px-4 sm:px-6 safe-padding border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-6 sm:mb-8 lowercase text-center text-neutral-900">how it works</h2>
          
          <div className="space-y-6 sm:space-y-8 md:space-y-10">
            {/* Step 1 */}
            <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 lg:gap-8 items-center pb-6 sm:pb-8 border-b border-neutral-200">
              <div className="space-y-2 sm:space-y-3 text-center lg:text-left order-2 lg:order-1">
                <div className="inline-flex items-center justify-center lg:justify-start gap-2 sm:gap-2.5 w-full lg:w-auto">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#cef7fc] text-neutral-900 flex items-center justify-center font-black text-base sm:text-lg flex-shrink-0">1</div>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-black lowercase text-neutral-900">make your circle</h3>
                </div>
                <p className="text-sm sm:text-base text-neutral-600 leading-relaxed max-w-md mx-auto lg:mx-0">
                  add 2–6 friends, set your vibe, pick what you're into.
                </p>
              </div>
              <div className="relative aspect-[9/19] w-full max-w-[180px] sm:max-w-[220px] md:max-w-[260px] mx-auto order-1 lg:order-2">
                <div className="absolute inset-0 bg-gradient-to-br from-neutral-700 to-neutral-800 rounded-[2rem] sm:rounded-[2.5rem] border-2 border-neutral-600 p-1.5 shadow-xl shadow-black/35">
                  <div className="w-full h-full bg-[#0b0b0b] rounded-[1.75rem] sm:rounded-[2rem] overflow-hidden relative">
                    <img 
                      src="/images/screenshots/step1-make-circle.png" 
                      alt="Make your circle" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 lg:gap-8 items-center pb-6 sm:pb-8 border-b border-neutral-200">
              <div className="relative aspect-[9/19] w-full max-w-[180px] sm:max-w-[220px] md:max-w-[260px] mx-auto order-1">
                <div className="absolute inset-0 bg-gradient-to-br from-neutral-700 to-neutral-800 rounded-[2rem] sm:rounded-[2.5rem] border-2 border-neutral-600 p-1.5 shadow-xl shadow-black/35">
                  <div className="w-full h-full bg-[#0b0b0b] rounded-[1.75rem] sm:rounded-[2rem] overflow-hidden relative">
                    <img 
                      src="/images/screenshots/step2-swipe-circles.png" 
                      alt="Swipe circles" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2 sm:space-y-3 text-center lg:text-left order-2">
                <div className="inline-flex items-center justify-center lg:justify-start gap-2 sm:gap-2.5 w-full lg:w-auto">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#cef7fc] text-neutral-900 flex items-center justify-center font-black text-base sm:text-lg flex-shrink-0">2</div>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-black lowercase text-neutral-900">swipe circles</h3>
                </div>
                <p className="text-sm sm:text-base text-neutral-600 leading-relaxed max-w-md mx-auto lg:mx-0">
                  find nearby groups with similar energy. vote together as a circle.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 lg:gap-8 items-center">
              <div className="space-y-2 sm:space-y-3 text-center lg:text-left order-2 lg:order-1">
                <div className="inline-flex items-center justify-center lg:justify-start gap-2 sm:gap-2.5 w-full lg:w-auto">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#cef7fc] text-neutral-900 flex items-center justify-center font-black text-base sm:text-lg flex-shrink-0">3</div>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-black lowercase text-neutral-900">make a plan</h3>
                </div>
                <p className="text-sm sm:text-base text-neutral-600 leading-relaxed max-w-md mx-auto lg:mx-0">
                  chat opens. send polls, make decisions together.
                </p>
              </div>
              <div className="relative aspect-[9/19] w-full max-w-[180px] sm:max-w-[220px] md:max-w-[260px] mx-auto order-1 lg:order-2">
                <div className="absolute inset-0 bg-gradient-to-br from-neutral-700 to-neutral-800 rounded-[2rem] sm:rounded-[2.5rem] border-2 border-neutral-600 p-1.5 shadow-xl shadow-black/35">
                  <div className="w-full h-full bg-[#0b0b0b] rounded-[1.75rem] sm:rounded-[2rem] overflow-hidden relative">
                    <img 
                      src="/images/screenshots/step3-group-chat.png" 
                      alt="Group chat" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why You Need It */}
      <section className="py-8 sm:py-12 md:py-16 px-4 sm:px-6 safe-padding border-t border-gray-200 bg-gradient-to-b from-white to-[#cef7fc]/20">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-6 sm:mb-8 lowercase text-center leading-tight text-neutral-900">meet new groups together</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <Feature 
              title="go together"
              desc="bring your friends, meet as a group"
              icon={<Users className="h-5 w-5 sm:h-6 sm:w-6" />}
            />
            <Feature 
              title="decide together"
              desc="vote as a circle, chat, send polls"
              icon={<MessageSquare className="h-5 w-5 sm:h-6 sm:w-6" />}
            />
            <Feature 
              title="make plans"
              desc="lock in activities, make it happen"
              icon={<Check className="h-5 w-5 sm:h-6 sm:w-6" />}
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="ready-to-start" className="py-8 sm:py-12 md:py-16 px-4 sm:px-6 safe-padding border-t border-gray-200">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-2 sm:mb-3 lowercase text-neutral-900">ready to start?</h2>
          <p className="text-sm sm:text-base text-neutral-500 mb-5 sm:mb-6">
            grab your friends and meet another circle
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://apps.apple.com/us/app/circles-meet-groups-together/id6757444421"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-neutral-900 hover:bg-black text-white px-5 py-3 rounded-xl font-medium transition-all active:scale-[0.98] shadow-lg shadow-[#cef7fc]/40 hover:shadow-xl hover:shadow-[#cef7fc]/50 ring-1 ring-[#cef7fc]/20"
            >
              <svg className="h-8 w-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <div className="flex flex-col items-start">
                <span className="text-[10px] leading-none opacity-80">Download on the</span>
                <span className="text-lg leading-tight font-semibold -mt-0.5">App Store</span>
              </div>
            </a>
            <div
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-neutral-900/60 text-white/70 px-5 py-3 rounded-xl font-medium cursor-default"
            >
              <svg className="h-7 w-7 opacity-70" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              <div className="flex flex-col items-start">
                <span className="text-[10px] leading-none opacity-80">COMING SOON TO</span>
                <span className="text-lg leading-tight font-semibold -mt-0.5">Google Play</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-6 sm:py-8 md:py-10 px-4 sm:px-6 safe-padding bg-white">
        <div className="max-w-6xl mx-auto flex flex-col items-center gap-4 sm:gap-6">
          <div className="text-xl font-black lowercase text-neutral-900">circles</div>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm text-neutral-500">
            <Link to="/privacy" className="hover:text-gray-900 transition lowercase">privacy</Link>
            <Link to="/terms" className="hover:text-gray-900 transition lowercase">terms</Link>
            <a href="mailto:hello@getcircles.com" className="hover:text-gray-900 transition lowercase">support</a>
          </div>
          <p className="text-gray-400 text-xs sm:text-sm">
            © {new Date().getFullYear()} circles
          </p>
        </div>
      </footer>
    </div>
  );
}

// Circles Icon Component
function CirclesIcon({ size = 28, color = '#cef7fc', isActive = false }) {
  const outerStrokeWidth = 1.2;
  const innerStrokeWidth = 0.9;
  
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      style={{ display: 'block' }}
    >
      {isActive ? (
        <>
          <path 
            d="M14.92,16.72c2.69.92,6.1-1.5,6.12-4.41.19-2.8-2.16-5.14-4.97-5.16-.47-.01-.85.07-1,.28-.24.31.05,1,.23,1.53.79,2.38.47,5-.47,7.27-.02.13-.03.29.03.42l.06.07Z" 
            fill={color} 
            stroke={color} 
            strokeLinecap="round" 
            strokeMiterlimit="10" 
            strokeWidth={outerStrokeWidth}
          />
          <path 
            d="M12.35,18.12c1.18.72,2.57,1.11,4.05,1.04,3.96-.17,7.03-3.52,6.85-7.48s-3.52-7.03-7.48-6.85c-1.26.05-2.42.43-3.43,1.04" 
            fill="none" 
            stroke={color} 
            strokeLinecap="round" 
            strokeMiterlimit="10" 
            strokeWidth={innerStrokeWidth}
          />
          <circle 
            cx="7.22" 
            cy="11.89" 
            r="6.03" 
            fill={color} 
            stroke={color} 
            strokeLinecap="round" 
            strokeMiterlimit="10" 
            strokeWidth={outerStrokeWidth}
          />
        </>
      ) : (
        <>
          <path 
            d="M14.68,16.7c.48.14.99.21,1.51.2,2.69-.07,4.82-2.32,4.74-5.01s-2.32-4.82-5.01-4.74c-.4.01-.79.07-1.16.17" 
            fill="none" 
            stroke={color} 
            strokeLinecap="round" 
            strokeMiterlimit="10" 
            strokeWidth={outerStrokeWidth}
          />
          <path 
            d="M12.35,18.12c1.18.72,2.57,1.11,4.05,1.04,3.96-.17,7.03-3.52,6.85-7.48s-3.52-7.03-7.48-6.85c-1.26.05-2.42.43-3.43,1.04" 
            fill="none" 
            stroke={color} 
            strokeLinecap="round" 
            strokeMiterlimit="10" 
            strokeWidth={innerStrokeWidth}
          />
          <circle 
            cx="7.22" 
            cy="11.89" 
            r="6.03" 
            fill="none" 
            stroke={color} 
            strokeLinecap="round" 
            strokeMiterlimit="10" 
            strokeWidth={outerStrokeWidth}
          />
        </>
      )}
    </svg>
  );
}

// Chat Visual Component
function ChatVisual() {
  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Messages */}
      <div className="flex flex-col gap-2.5 sm:gap-3">
        {/* Other person message */}
        <div className="flex items-start gap-2">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex-shrink-0" />
          <div className="bg-white/10 rounded-2xl rounded-tl-sm px-3 sm:px-4 py-2 max-w-[85%] sm:max-w-xs">
            <p className="text-xs sm:text-sm text-white/90">hey! want to grab coffee this weekend?</p>
          </div>
        </div>

        {/* Poll message */}
        <div className="flex items-start gap-2">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex-shrink-0" />
          <div className="bg-white/10 rounded-2xl rounded-tl-sm px-3 sm:px-4 py-2.5 sm:py-3 max-w-[85%] sm:max-w-xs">
            <div className="flex items-center gap-2 mb-2.5 sm:mb-3">
              <BarChart3 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#cef7fc]" />
              <p className="text-xs sm:text-sm font-semibold text-white/90">where should we meet?</p>
            </div>
            <div className="space-y-2">
              <div className="bg-[#cef7fc]/20 rounded-lg px-2.5 sm:px-3 py-1.5 sm:py-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] sm:text-xs text-white/80">blue bottle</span>
                  <span className="text-[10px] sm:text-xs text-white/60">3 votes</span>
                </div>
                <div className="h-1 sm:h-1.5 bg-[#cef7fc]/30 rounded-full overflow-hidden">
                  <div className="h-full bg-[#cef7fc] rounded-full" style={{ width: '75%' }} />
                </div>
              </div>
              <div className="bg-white/5 rounded-lg px-2.5 sm:px-3 py-1.5 sm:py-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] sm:text-xs text-white/70">philz</span>
                  <span className="text-[10px] sm:text-xs text-white/50">1 vote</span>
                </div>
                <div className="h-1 sm:h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-white/30 rounded-full" style={{ width: '25%' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* My message */}
        <div className="flex items-start gap-2 justify-end">
          <div className="bg-[#cef7fc] rounded-2xl rounded-tr-sm px-3 sm:px-4 py-2 max-w-[85%] sm:max-w-xs">
            <p className="text-xs sm:text-sm text-gray-900">sounds good! saturday works</p>
          </div>
        </div>
      </div>
    </div>
  );
}



// Feature Component
function Feature({ title, desc, icon }) {
  return (
    <div className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 border border-neutral-200 text-center shadow-sm hover:shadow-md transition-shadow">
      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#cef7fc] flex items-center justify-center mx-auto mb-3 sm:mb-4">
        {icon ? React.cloneElement(icon, { className: "h-5 w-5 sm:h-6 sm:w-6 text-neutral-800" }) : <Check className="h-5 w-5 sm:h-6 sm:w-6 text-neutral-800" />}
      </div>
      <h3 className="text-base sm:text-lg font-bold mb-1.5 sm:mb-2 lowercase text-neutral-900">{title}</h3>
      <p className="text-neutral-500 text-xs sm:text-sm">{desc}</p>
    </div>
  );
}

// Feature Item Component
function FeatureItem({ text }) {
  return (
    <div className="flex items-center gap-2.5 sm:gap-3">
      <div className="w-2 h-2 rounded-full bg-neutral-900 flex-shrink-0" />
      <span className="text-neutral-600 text-sm sm:text-base">{text}</span>
    </div>
  );
}

// Hero Phone Visual - Animated circles passing by
function HeroPhoneVisual() {
  const [currentCircleIndex, setCurrentCircleIndex] = useState(0);
  const [animationKey, setAnimationKey] = useState(0);
  
  // Two group images that rotate - place girls1.png and boys1.png in public/images/
  const CIRCLE_IMAGES = [
    '/images/girls1.png',
    '/images/boys1.png',
    '/images/girls2.png',
    '/images/girls3.png',
    '/images/girls4.png',
    '/images/boys2.png',
    '/images/girls5.png',
  ];
  
  // Cycle through circles - slightly shorter than animation for overlap
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCircleIndex((prev) => (prev + 1) % CIRCLE_IMAGES.length);
      setAnimationKey((prev) => prev + 1);
    }, 3200);

    return () => clearInterval(interval);
  }, [CIRCLE_IMAGES.length]);
  
  const currentCircle = CIRCLE_IMAGES[currentCircleIndex];
  
  return (
    <div className="w-full max-w-[220px] sm:max-w-[260px] lg:max-w-[300px] relative">
      {/* Floating Circle Image - positioned to overlap phone nicely */}
      <div 
        className="absolute top-[8%] sm:top-[5%] left-1/2 -translate-x-1/2 z-20 pointer-events-none"
        key={`popout-${animationKey}`}
      >
        <div className="circle-swipe-away">
          {/* The actual circle image - transparent PNG, responsive sizing */}
          <img 
            src={currentCircle}
            alt="Circle"
            className="max-w-none relative z-10 w-[260px] sm:w-[280px] lg:w-[360px]"
            style={{
              filter: 'drop-shadow(0 16px 40px rgba(0, 0, 0, 0.3))',
            }}
            onError={(e) => {
              // Hide the broken image and show placeholder
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
          {/* Fallback circle placeholder if image not found */}
          <div 
            className="w-[220px] h-[220px] sm:w-[280px] sm:h-[280px] lg:w-[340px] lg:h-[340px] rounded-full items-center justify-center hidden"
            style={{
              background: 'linear-gradient(135deg, #1f2937, #374151)',
              boxShadow: '0 16px 80px rgba(206, 247, 252, 0.6), 0 0 120px rgba(206, 247, 252, 0.3), 0 30px 60px rgba(0, 0, 0, 0.4)',
              border: '5px solid rgba(206, 247, 252, 0.7)',
            }}
          >
            <span className="text-white/30 text-sm">Circle Image</span>
          </div>
        </div>
      </div>
      
      {/* Phone Frame */}
      <div className="relative aspect-[9/19] bg-gradient-to-br from-neutral-700 to-neutral-800 rounded-[2.5rem] sm:rounded-[3rem] border-2 border-neutral-600 p-1.5 sm:p-2 shadow-2xl shadow-black/35">
        <div className="w-full h-full bg-gray-900 rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden relative">
          {/* App Screenshot - Your actual screenshot goes here */}
          <img 
            src="/images/screenshots/hero.png" 
            alt="Circles app" 
            className="w-full h-full object-cover"
            onError={(e) => {
              // If no screenshot, show placeholder
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
          {/* Fallback placeholder if screenshot not available */}
          <div
            className="w-full h-full items-center justify-center bg-black"
            style={{ display: 'none' }}
          >
            <div className="text-center p-6">
              <p className="text-white/30 text-sm">Add your screenshot at:</p>
              <p className="text-white/50 text-xs mt-2 font-mono">/images/screenshots/hero.png</p>
            </div>
          </div>
        </div>
        
        {/* Phone notch */}
        <div className="absolute top-3 sm:top-4 left-1/2 -translate-x-1/2 w-20 sm:w-24 h-5 sm:h-6 bg-black rounded-full" />
      </div>
      
      {/* Transparent overlay image for pop-out effect */}
      {/* This creates the illusion of the circle popping out of the phone */}
      <img 
        src="/images/screenshots/hero-overlay.png" 
        alt="" 
        className="absolute inset-0 w-full h-full object-contain pointer-events-none z-30"
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
    </div>
  );
}

// Separate Circles Visual - NO overlapping
function SeparateCirclesVisual({ topPhotos, bottomPhotos, scale = 1 }) {
  return (
    <div 
      className="relative w-full h-full flex flex-col items-center justify-center gap-6 sm:gap-8 md:gap-12"
      style={{ transform: `scale(${scale})` }}
    >
      {/* Top Circle - Another group */}
      <div className="relative">
        <div className="text-center mb-2 sm:mb-4">
          <p className="text-[10px] sm:text-xs text-white/40 uppercase tracking-wider">another circle</p>
        </div>
        <CircleRingVisual photos={topPhotos} size={100} avatarSize={32} opacity={0.95} />
      </div>
      
      {/* Bottom Circle - Your circle */}
      <div className="relative">
        <div className="text-center mb-2 sm:mb-4">
          <p className="text-[10px] sm:text-xs text-white/40 uppercase tracking-wider">your circle</p>
        </div>
        <CircleRingVisual photos={bottomPhotos} size={100} avatarSize={32} opacity={0.9} showCenter={true} />
      </div>
    </div>
  );
}

// Circular Ring Visual Component - matches app design
function CircleRingVisual({ photos, size = 100, avatarSize = 32, opacity = 1, showCenter = false, isFloating = false }) {
  const radius = size;
  const numPhotos = photos.length;
  const angleStep = (360 / numPhotos);
  const avatarRadius = radius - (size * 0.24); // Proportional to size
  
  return (
    <div 
      className="relative"
      style={{ 
        width: radius * 2, 
        height: radius * 2,
        opacity
      }}
    >
      {/* Glass Blur Background for floating circles */}
      {isFloating && (
        <div 
          className="absolute inset-0 rounded-full"
          style={{
            background: 'rgba(206, 247, 252, 0.08)',
            backdropFilter: 'blur(20px) saturate(150%)',
            WebkitBackdropFilter: 'blur(20px) saturate(150%)',
            border: '2px solid rgba(206, 247, 252, 0.3)',
            boxShadow: '0 8px 32px rgba(206, 247, 252, 0.3), 0 0 60px rgba(206, 247, 252, 0.15)',
          }}
        />
      )}
      
      {/* Glass Blur Background */}
      {showCenter && !isFloating && (
        <div 
          className="absolute inset-0 rounded-full"
          style={{
            background: 'rgba(255, 255, 255, 0.06)',
            backdropFilter: 'blur(50px) saturate(180%)',
            WebkitBackdropFilter: 'blur(50px) saturate(180%)',
            border: '1.5px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 4px 18px rgba(0, 0, 0, 0.05)',
          }}
        />
      )}
      
      {/* SVG Ring Borders */}
      <svg 
        width={radius * 2} 
        height={radius * 2} 
        className="absolute inset-0"
        style={{ overflow: 'visible' }}
      >
        <circle
          cx={radius}
          cy={radius}
          r={radius - 6}
          fill="none"
          stroke={isFloating ? "rgba(206, 247, 252, 0.4)" : "#374151"}
          strokeWidth={isFloating ? "3" : "6"}
        />
        <circle
          cx={radius}
          cy={radius}
          r={radius - 9}
          fill="none"
          stroke="#cef7fc"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
      
      {/* Avatars */}
      {photos.map((photo, index) => {
        const angle = (index * angleStep - 90) * (Math.PI / 180);
        const x = radius + avatarRadius * Math.cos(angle);
        const y = radius + avatarRadius * Math.sin(angle);
        
        return (
          <div
            key={index}
            className="absolute rounded-full overflow-hidden"
            style={{
              width: avatarSize,
              height: avatarSize,
              left: x - avatarSize / 2,
              top: y - avatarSize / 2,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(0, 0, 0, 0.1)',
              transform: 'translateZ(0)',
            }}
          >
            <img 
              src={photo}
              alt={`Person ${index + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        );
      })}
      
      {/* Center Core */}
      {showCenter && (
        <div 
          className="absolute inset-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#cef7fc]/50 backdrop-blur-sm flex items-center justify-center"
          style={{
            width: '36px',
            height: '36px',
            background: 'rgba(206, 247, 252, 0.4)',
          }}
        >
          <Circle className="h-4 w-4 text-[#cef7fc]" />
        </div>
      )}
    </div>
  );
}
